// @noflow

import { uniqBy, isEqual, unescape } from 'lodash';
import { updateTimersList } from './utils';
import {
  SKIP_OPT_VALUE_SKIPPED,
  SKIP_OPT_VALUE_SCHEDULED,
  SKIP_OPT_VALUE_CONFLICTS,
} from './TimerTypes';
import type { Action, State, Timer, DiskInfo } from './types';

function calculateDiskUsage(diskInfo: ?DiskInfo): ?number {
  let diskUsage;

  if (diskInfo && diskInfo.free_sd_mins !== undefined && diskInfo.total_sd_mins) {
    diskUsage = 100 - Math.round((diskInfo.free_sd_mins * 100) / diskInfo.total_sd_mins);
  }

  return diskUsage;
}

export const initialState: State = {
  recordings: {
    content: [],
    error: false,
    fetching: false,
    filters: null,
  },
  timers: {
    content: [],
    fetching: false,
    error: false,
    loaded: false,
    filteredTimers: [],
  },
  scheduled: {
    content: [],
    fetching: false,
    error: false,
    loaded: false,
  },
  recordedAirings: [],
  recordingsDetails: {
    content: [],
    fetching: false,
    error: false,
  },
  deletedRecordings: [],
  deleteRecording: {
    fetching: false,
    error: false,
  },
  createTimer: {
    fetching: false,
    error: false,
    content: {},
    pendingCreate: [],
  },
  updating: {
    fetching: false,
    error: false,
    pendingUpdate: [],
    pendingUpdateData: [],
  },
  deleteTimer: {
    fetching: false,
    error: false,
    pendingRemoval: [],
  },
  deletedTimers: [],
  skippingOrRestoring: {
    fetching: false,
    error: false,
  },
  setError: false,
  dvrUsageInPercents: undefined,
  dvrNumberOfRecordings: undefined,
  fetchingReceiverStatus: false,
  dataGeneration: 0,
  config: {
    recordingsViewMode: 'grid',
    ptatViewMode: 'grid',
    recordingsSort: 'date',
    ptatSort: 'date',
  },
};

const updateTimerScheduleOptions = (timer: Timer, timers: Array<Timer>): Array<Timer> =>
  timers.map(t => {
    if (t.dtimer_id === timer.dtimer_id) {
      const skipped = [SKIP_OPT_VALUE_SCHEDULED, SKIP_OPT_VALUE_CONFLICTS].includes(timer.skip_opt);
      const overwriteValues = skipped
        ? {
            skip_opt: SKIP_OPT_VALUE_SKIPPED,
            filter: 'skipped',
          }
        : {
            skip_opt: SKIP_OPT_VALUE_SCHEDULED,
            filter: 'scheduled',
          };
      return { ...t, ...overwriteValues };
    }
    return t;
  });

const resetContentOnFiltersChanged = (recordingsState, oldFilters, newFilters) =>
  isEqual(oldFilters, newFilters) ? recordingsState : { ...recordingsState, content: [] };

const filterDeletedEpisode = (recordings, id, eventListing) => {
  if (eventListing != null) {
    // use seriesId here, because slug might be different in recordings and franchises
    const { seriesId } = eventListing;
    const dvr = recordings.find(item => item.series_id === seriesId);
    if (dvr != null) {
      dvr.count -= 1;
    }
  }
  return recordings.filter(item => item.count > 0);
};

const reducer = (state: State = initialState, action: Action): State => {
  switch (action.type) {
    case 'REQUEST_RECORDINGS':
      return {
        ...state,
        recordings: {
          ...resetContentOnFiltersChanged(
            state.recordings,
            state.recordings.filters,
            action.filters,
          ),
          fetching: true,
          filters: action.filters,
        },
      };
    case 'REQUEST_RECORDINGS_DETAILS':
      return {
        ...state,
        recordingsDetails: {
          ...state.recordingsDetails,
          fetching: true,
        },
      };
    case 'RECEIVE_RECORDINGS': {
      const { payload } = action;
      return {
        ...state,
        recordings: {
          content: Array.isArray(payload) ? payload : [],
          fetching: false,
          error: false,
          filters: action.filters,
        },
      };
    }
    case 'RECEIVE_RECORDINGS_DETAILS': {
      const { payload } = action;
      return {
        ...state,
        recordingsDetails: {
          content: Array.isArray(payload) ? payload : [],
          fetching: false,
          error: false,
        },
      };
    }
    case 'REQUEST_RECORDINGS_ERROR': {
      return {
        ...state,
        recordings: {
          ...resetContentOnFiltersChanged(
            state.recordings,
            state.recordings.filters,
            action.filters,
          ),
          fetching: false,
          error: true,
          filters: action.filters,
        },
      };
    }
    case 'REQUEST_RECORDINGS_DETAILS_ERROR':
      return {
        ...state,
        recordingsDetails: {
          ...state.recordingsDetails,
          fetching: false,
          error: true,
        },
      };
    case 'REQUEST_TIMERS':
      return {
        ...state,
        timers: {
          ...state.timers,
          fetching: true,
        },
        scheduled: {
          ...state.scheduled,
          fetching: true,
        },
      };
    case 'REQUEST_TIMERS_ERROR':
      return {
        ...state,
        timers: {
          ...state.timers,
          fetching: false,
          error: true,
          loaded: true,
        },
        scheduled: {
          ...state.scheduled,
          fetching: false,
          error: true,
          loaded: true,
        },
      };
    case 'RECEIVE_TIMERS': {
      const { payload } = action;
      /*
       * Backend returns a list of timers including duplicates where 'dtimer_id', 'gtimer_id' and 'skip_opt' fields are missed,
       * that's why we should remove these corrupted timers from list. Should be removed in future.
       * TODO:
       *  [DE7020] https://rally1.rallydev.com/#/116542103696ud/detail/defect/263831022512,
       *  [DE8537] https://rally1.rallydev.com/#/233447464096ud/detail/defect/288615551996
       *
       * Also removed already deleted timers
       */
      const scheduled = Array.isArray(payload)
        ? uniqBy(
            payload.filter(
              item =>
                item.dtimer_id !== undefined &&
                item.skip_opt !== undefined &&
                item.gtimer_id !== undefined &&
                !state.deletedTimers.includes(item.gtimer_id),
            ),
            timer => timer.dtimer_id,
          )
        : [];

      /*
       * Backend doesn't returns a separate list of timers which should be shown in "Timers" page on Mobile and "My Timers" section on Web,
       * and as per Dave comments: we can differentiate between the types by the presence of the dtimer_id attribute, so that if dtimer_id
       * is NOT present then that what we need. No plans to change this somehow in the future.
       */
      const timers = Array.isArray(payload)
        ? payload.filter(item => item.dtimer_id === undefined)
        : [];

      // some receivers return timer names with escaped characters
      timers.forEach(timer => {
        timer.event_name = unescape(timer.event_name);
      });

      return {
        ...state,
        timers: {
          content: timers,
          fetching: false,
          error: false,
          loaded: true,
        },
        scheduled: {
          content: scheduled,
          fetching: false,
          error: false,
          loaded: true,
        },
      };
    }
    case 'REQUEST_DELETE_RECORDING':
      return {
        ...state,
        deleteRecording: {
          fetching: true,
          error: false,
        },
      };
    case 'RECEIVE_DELETE_RECORDING': {
      const { id, eventListing } = action;
      return {
        ...state,
        recordingsDetails: {
          ...state.recordingsDetails,
          content: state.recordingsDetails.content.filter(item => item.id !== id),
        },
        recordings: {
          ...state.recordings,
          content: filterDeletedEpisode(state.recordings.content, id, eventListing),
        },
        deleteRecording: {
          fetching: false,
          error: false,
        },
        deletedRecordings: [...state.deletedRecordings, id],
      };
    }
    case 'REQUEST_DELETE_RECORDING_ERROR':
      return {
        ...state,
        deleteRecording: {
          fetching: false,
          error: true,
        },
      };
    case 'REQUEST_DELETE_MULTIPLE_RECORDINGS':
      return {
        ...state,
        deleteRecording: {
          fetching: true,
          error: false,
        },
      };
    case 'RECEIVE_DELETE_MULTIPLE_RECORDINGS':
      return {
        ...state,
        deleteRecording: {
          fetching: false,
          error: false,
        },
      };
    case 'REQUEST_DELETE_MULTIPLE_RECORDINGS_ERROR':
      return {
        ...state,
        deleteRecording: {
          fetching: false,
          error: true,
        },
      };
    case 'REQUEST_CREATE_TIMER':
      return {
        ...state,
        createTimer: {
          fetching: true,
          error: false,
          content: {},
          pendingCreate: [...state.createTimer.pendingCreate, action.airingId],
        },
      };
    case 'RECEIVE_CREATE_TIMER':
      // eslint-disable-next-line
      const recordedAirings = action.payload.identifier
        ? [...state.recordedAirings, action.payload.identifier]
        : state.recordedAirings;
      return {
        ...state,
        createTimer: {
          fetching: false,
          error: false,
          content: {},
          pendingCreate: state.createTimer.pendingCreate.filter(id => id !== action.airingId),
        },
        recordedAirings,
      };
    case 'REQUEST_CREATE_TIMER_ERROR':
      return {
        ...state,
        createTimer: {
          fetching: false,
          error: true,
          content: action.payload,
          pendingCreate: state.createTimer.pendingCreate.filter(id => id !== action.airingId),
        },
      };
    case 'REQUEST_UPDATE_TIMER': {
      const {
        payload: { gTimerId, timer },
      } = action;
      return {
        ...state,
        updating: {
          fetching: true,
          error: false,
          pendingUpdate: [...state.updating.pendingUpdate, gTimerId],
          pendingUpdateData: [...state.updating.pendingUpdateData, timer],
        },
      };
    }
    case 'RECEIVE_UPDATE_TIMER': {
      const {
        payload: { gTimerId, updatedTimer },
      } = action;
      return {
        ...state,
        updating: {
          fetching: false,
          error: false,
          pendingUpdate: state.updating.pendingUpdate.filter(id => id !== gTimerId),
          pendingUpdateData: state.updating.pendingUpdateData.filter(item => item !== updatedTimer),
        },
        timers: {
          ...state.timers,
          content: updateTimersList(state.timers.content, updatedTimer, gTimerId),
        },
      };
    }
    case 'REQUEST_UPDATE_TIMER_ERROR': {
      const {
        payload: { gTimerId, timer },
      } = action;
      return {
        ...state,
        updating: {
          fetching: false,
          error: true,
          pendingUpdate: state.updating.pendingUpdate.filter(id => id !== gTimerId),
          pendingUpdateData: state.updating.pendingUpdateData.filter(item => item !== timer),
        },
      };
    }
    case 'REQUEST_DELETE_TIMER': {
      const {
        payload: { gTimerId },
      } = action;

      const { pendingRemoval } = state.deleteTimer;
      return {
        ...state,
        deleteTimer: {
          fetching: true,
          error: false,
          pendingRemoval: [...pendingRemoval, gTimerId],
        },
      };
    }
    case 'RECEIVE_DELETE_TIMER': {
      const { content: timers } = state.timers;
      const { content: scheduled } = state.scheduled;
      const { deletedTimers } = state;
      const { pendingRemoval } = state.deleteTimer;

      const {
        payload: { gTimerId },
      } = action;
      return {
        ...state,
        deleteTimer: {
          fetching: false,
          error: false,
          pendingRemoval: pendingRemoval.filter(
            (gTimerIdInQueue: number) => gTimerIdInQueue !== gTimerId,
          ),
        },
        timers: {
          ...state.timers,
          content: timers.filter((timer: Timer) => timer.gtimer_id !== gTimerId),
        },
        scheduled: {
          ...state.scheduled,
          content: scheduled.filter((timer: Timer) => timer.gtimer_id !== gTimerId),
        },
        deletedTimers: [...deletedTimers, gTimerId],
        recordedAirings: [],
      };
    }
    case 'REQUEST_DELETE_TIMER_ERROR':
      return {
        ...state,
        deleteTimer: {
          ...state.deleteTimer,
          fetching: false,
          error: true,
        },
      };
    case 'REQUEST_SKIP_RESTORE_TIMER':
      return {
        ...state,
        skippingOrRestoring: {
          fetching: true,
          error: false,
        },
      };
    case 'RECEIVE_SKIP_RESTORE_TIMER_SUCCESS':
      return {
        ...state,
        skippingOrRestoring: {
          fetching: false,
          error: false,
        },
        scheduled: {
          ...state.scheduled,
          content: updateTimerScheduleOptions(action.timer, state.scheduled.content),
        },
      };
    case 'REQUEST_SKIP_RESTORE_TIMER_ERROR':
      return {
        ...state,
        skippingOrRestoring: {
          fetching: false,
          error: false,
        },
      };
    case 'REQUEST_RECEIVER_INFO':
      return state;
    case 'RECEIVE_RECEIVER_INFO': {
      const { disk_info: diskInfo, total_recordings: totalRecordings } = action.payload;
      return {
        ...state,
        dvrUsageInPercents: calculateDiskUsage(diskInfo),
        dvrNumberOfRecordings: totalRecordings,
      };
    }
    case 'REQUEST_RECEIVER_INFO_ERROR':
      return {
        ...state,
        dvrUsageInPercents: undefined,
        dvrNumberOfRecordings: undefined,
      };
    // $FlowExpectedError
    case 'CHANGE_ACTIVE_RECEIVER_SUCCESS':
      /* We must clean all user and STB related data from store on sign-out */
      return {
        ...state,
        recordings: {
          content: [],
          error: false,
          fetching: false,
          filters: null,
        },
        timers: {
          content: [],
          fetching: false,
          error: false,
          loaded: false,
        },
        scheduled: {
          content: [],
          fetching: false,
          error: false,
          loaded: false,
        },
        recordingsDetails: {
          content: [],
          fetching: false,
          error: false,
        },
        dvrUsageInPercents: undefined,
        dvrNumberOfRecordings: undefined,
        dataGeneration: state.dataGeneration + 1,
      };
    case 'REQUEST_RECEIVERS_STATUS':
      return { ...state, fetchingReceiverStatus: true };
    case 'RECEIVE_RECEIVERS_STATUS':
      return { ...state, fetchingReceiverStatus: false };
    case 'REQUEST_RECEIVERS_STATUS_ERROR':
      return { ...state, fetchingReceiverStatus: false };
    // $FlowExpectedError
    case 'SIGN_OUT':
      return initialState;
    case 'SOCKET_DVR_CHANGED':
      // We've received a DVR data domain event from the receiver. Something's changed in DVR data.
      return {
        ...state,
        dataGeneration: state.dataGeneration + 1,
      };
    case 'CHANGE_RECORDINGS_VIEW_MODE': {
      return {
        ...state,
        config: {
          ...state.config,
          recordingsViewMode: action.viewMode,
        },
      };
    }
    case 'CHANGE_PTAT_VIEW_MODE': {
      return {
        ...state,
        config: {
          ...state.config,
          ptatViewMode: action.viewMode,
        },
      };
    }
    case 'CHANGE_RECORDINGS_SORT': {
      return {
        ...state,
        config: {
          ...state.config,
          recordingsSort: action.sort,
        },
      };
    }
    case 'CHANGE_PTAT_SORT': {
      return {
        ...state,
        config: {
          ...state.config,
          ptatSort: action.sort,
        },
      };
    }
    default:
      /* Following line assures switch exhaustiveness */
      (action.type: empty); /* eslint-disable-line */
      return state;
  }
};

export default reducer;
